import React from "react";

import {
	CombinedTransplant,
	Status,
	Urgency
} from "lib/build/forms/ltx/patientCondition";
import {
	ab0Messages,
	rhMessages
} from "lib/build/forms/ltx/phase-i/bloodGroup";

import { computeAge } from "src/common/hooks/useFormatAge";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { useFormatName } from "src/common/hooks/useFormatName";
import { getNumberOfDays } from "src/common/utils/dates";
import { sexMessages, yesNoMessages } from "src/messages/generic";
import {
	longStatusMessages,
	shortStatusMessages
} from "src/modules/ltx/messages";
import { queries } from "src/modules/ltx/patient-list/queries";
import { Patient } from "src/modules/ltx/patient-list/types";
import { findEvent, RawPatient } from "src/modules/shared/patient-list";

export function useNormalizePatient(): (patient: RawPatient) => Patient {
	const formatMessage = useFormatMessageMap();
	const formatName = useFormatName();

	return React.useCallback(
		(rawPatient: RawPatient): Patient => {
			const {
				id,
				socialSecurityNumber,
				title = "",
				firstName,
				lastName,
				sex,
				dateOfBirth,
				extra
			} = rawPatient;

			const statusEvent = findEvent<string>(extra, queries.status);
			const addressEvent = findEvent<{ city: string }>(
				extra,
				queries.address
			);
			const weightEvent = findEvent<number>(extra, queries.weight);
			const ab0Event = findEvent<string>(extra, queries.ab0);
			const rhEvent = findEvent<string>(extra, queries.rh);

			const waitingListEvent = findEvent<Date>(
				extra,
				queries.waitingListStartDate
			);
			const urgencyEvent = findEvent<string>(extra, queries.urgency);
			const combinedTransplantEvent = findEvent<string>(
				extra,
				queries.combinedTransplant
			);

			const { value: statusValue = Status.NEW } = statusEvent || {};
			const status = {
				value: statusValue,
				shortLabel: formatMessage(shortStatusMessages, statusValue),
				longLabel: formatMessage(longStatusMessages, statusValue)
			};

			const name = formatName({
				title,
				firstName,
				lastName
			});

			let bloodGroup = "";
			if (ab0Event !== null && rhEvent !== null) {
				const ab0Label = formatMessage(ab0Messages, ab0Event.value);
				const rhLabel = formatMessage(rhMessages, rhEvent.value);
				bloodGroup = `${ab0Label} RH ${rhLabel}`;
			}

			let waitingListDays: number | undefined;
			if (waitingListEvent !== null) {
				waitingListDays = getNumberOfDays(
					waitingListEvent.value,
					new Date()
				);
			}

			const { value: urgencyValue = Urgency.NO } = urgencyEvent || {};
			const { value: combinedTransplantValue = CombinedTransplant.NO } =
				combinedTransplantEvent || {};

			return {
				id,

				status,
				name,
				socialSecurityNumber,

				age: computeAge(dateOfBirth),
				sex: formatMessage(sexMessages, sex),
				city: addressEvent === null ? "" : addressEvent.value.city,
				weight: weightEvent,
				bloodGroup,

				waitingListDays,
				urgency: urgencyValue,
				combinedTransplant: formatMessage(
					yesNoMessages,
					combinedTransplantValue
				)
			};
		},
		[formatMessage, formatName]
	);
}
